import React from 'react'
import LineSeparator from '../../../../../packages/kiti/components/helpers/LineSeparator'
import { COLORS } from '@btime/constants'
import Separator from '../../../../../components/pdf/Separator'
import { MONTHS } from '../../constants/dates'
import { findReplyByQuestionId } from '../../_helpers/pdfHelpers'
import { useServiceOrder } from '../../_load/ServiceOrderLoader'
import { Grid } from '../../../../../packages/kiti/components/structure/Grid'
import Image from '../../../../../components/pdf/Image'
import View from '../../../../../components/pdf/View'
import { BODY_ORIGIN_ID, useEasySolutionsPageNumbers } from '../../_shared/pagination'

const Content = () => {
  const { replies, place, endDate } = useServiceOrder()
  const question548 = findReplyByQuestionId(replies, 548)
  const question549 = findReplyByQuestionId(replies, 549)
  const question550 = findReplyByQuestionId(replies, 550)
  const question551 = findReplyByQuestionId(replies, 551)
  const question567 = findReplyByQuestionId(replies, 567)
  const question552 = findReplyByQuestionId(replies, 552)

  const question556 = findReplyByQuestionId(replies, 556)
  const question562 = findReplyByQuestionId(replies, 562)
  const question566 = findReplyByQuestionId(replies, 566)
  const question554 = findReplyByQuestionId(replies, 554)
  const question568 = findReplyByQuestionId(replies, 568)
  const question569 = findReplyByQuestionId(replies, 569)

  const question570 = findReplyByQuestionId(replies, 570)

  const question555 = findReplyByQuestionId(replies, 555)
  const question571 = findReplyByQuestionId(replies, 571)
  const question572 = findReplyByQuestionId(replies, 572)

  const currentMonth = new Date(question572?.value + 'T00:00:00').getMonth()
  const currentYear = new Date(question572?.value + 'T00:00:00').getFullYear()

  // Lista única das perguntas de "5. Manutenção preventiva realizada" — antes existiam
  // DUAS listas quase iguais (uma só para os números do Sumário, filtrada por
  // `Boolean(item?.value)`; outra para renderizar as seções de verdade, filtrada por
  // `q.data?.question?.name`), que podiam divergir em quantidade/ordem e por isso já
  // corriam o risco de o Sumário listar um "5.N" que não existe no corpo (ou vice-versa).
  // Unificada aqui na MESMA lista/filtro usados para renderizar o corpo (mais abaixo),
  // para os ids do Sumário sempre corresponderem 1:1 às âncoras (`<h3 id={q.id}>`)
  // realmente renderizadas.
  const manutencaoPreventivaQuestions = [
    { id: '556', data: question556 },
    { id: '562', data: question562 },
    { id: '568', data: question568 },
    { id: '569', data: question569 },
    { id: '566', data: question566 },
    { id: '554', data: question554 }
  ].filter(q => q.data?.question?.name)

  const manutencaoPreventivaSummaryItems = manutencaoPreventivaQuestions.map((q, index) => ({
    id: q.id,
    title: `5.${index + 1}. ${
      Array.isArray(q.data?.value) && q.data.value.length > 1 && !q.data.value?.[0]?.src
        ? 'Pontos Testados que Geraram Alarme na Central de Incêndio'
        : q.data?.question?.category?.name
    }`
  }))

  // Causa raiz do Sumário com números errados (reportado pela Julia Buzzo): o PR #779
  // envolveu cada título de seção (exceto "1. Introdução") com `<View pageBreakBefore>`,
  // que sempre empurra a seção para o topo de uma página nova — mas os números abaixo
  // continuaram cravados nos valores de antes daquele PR, e o conteúdo de cada seção (nº
  // de perguntas de "5." respondidas, nº de fotos anexadas) varia por Atividade, então
  // nenhum número fixo se mantém certo. `page` não é mais digitado à mão nem guardado em
  // `useState`: vem de `useEasySolutionsPageNumbers` (`_shared/pagination.js`), que mede a
  // posição real de cada âncora já renderizada — mesma técnica já usada em
  // `pdfs/bradesco/pgr/_helpers/pagination.js`. Sem estado/efeito: os ids/títulos abaixo
  // não dependem de nada além de `replies`, que já está disponível no primeiro render.
  const summaryItems = [
    { id: 'introducao', title: '1. Introdução' },
    { id: 'servicos', title: '2. Ferramentas Utilizadas' },
    {
      id: 'manutencao',
      title: '3. Manutenção do Sistema de Detecção e Alarme de Incêndio',
      children: [
        { id: 'central-incendio', title: '3.1. Central de Incêndio' },
        { id: 'detectores-fumaca', title: '3.2. Detectores de Fumaça' },
        { id: 'acionadores-manuais', title: '3.3. Acionadores Manuais' },
        { id: 'sirenes-modulo-comando', title: '3.4. Sirenes e Módulo de Comando' }
      ]
    },
    { id: 'teste-central', title: '4. Status da Central' },
    { id: 'saida', title: '5. Manutenção preventiva realizada', children: manutencaoPreventivaSummaryItems },
    { id: 'conclusao', title: '6. Conclusão' }
  ]

  // Ordem de documento das âncoras com `pageBreakBefore` (todas, exceto "introducao", que
  // já começa no topo da página por causa do `pageBreakAfter` do Sumário) — necessária
  // para o motor de paginação calcular o deslocamento acumulado corretamente. Os
  // subtítulos "3.1"-"3.4" NÃO entram aqui: não têm `<View pageBreakBefore>` (fluem
  // naturalmente dentro da página da Seção 3). Dentro de "5.", cada pergunta a partir da
  // segunda tem `pageBreakBefore` (`<View pageBreakBefore={index > 0}>` no JSX abaixo).
  const forcedBreakAnchorIds = [
    'servicos',
    'manutencao',
    'teste-central',
    'saida',
    ...manutencaoPreventivaQuestions.slice(1).map(q => q.id),
    'conclusao'
  ]

  const anchorIds = summaryItems.flatMap(item => [item.id, ...(item.children?.map(child => child.id) ?? [])])

  const pageNumbers = useEasySolutionsPageNumbers(anchorIds, forcedBreakAnchorIds)

  // Gap 4 (pedido da Julia Buzzo): sem fontWeight:'bold' forçado — o Sumário é uma lista de
  // links com "leaders" de número de página, não corpo de texto corrido, então mantemos
  // peso normal (herdando Arial 12pt do <div> raiz) e SEM o tratamento de justify/recuo de
  // 1ª linha usado no corpo do documento.
  const linkStyle = {
    color: 'black',
    textDecoration: 'none',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '7.5px'
  }

  const getFiles = question => {
    if (Array.isArray(question?.data?.value)) {
      return question.data.value.filter(item => item?.src)
    }
    return question?.data?.documents ?? []
  }

  const chunkArray = (arr, size) => {
    const result = []
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size))
    }
    return result
  }

  const renderImages = documents => {
    if (!documents) return null
    const groups = chunkArray(documents, 3)

    return groups.map((group, idx) => (
      <div
        key={idx}
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '16px',
          marginTop: '15px',
          flexWrap: 'wrap'
        }}
      >
        {group.map(doc => (
          <div
            key={doc.src}
            style={{
              textAlign: 'center',
              maxWidth: '220px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              backgroundColor: '#fff'
            }}
          >
            <Image
              src={doc.src}
              style={{
                height: '200px',
                width: '100%',
                objectFit: 'cover'
              }}
            />
            {doc?.description && (
              <div
                style={{
                  padding: '8px',
                  fontSize: '12pt',
                  textAlign: 'justify',
                  marginLeft: 0,
                  marginRight: 0,
                  textIndent: '1.25cm',
                  marginTop: 0,
                  marginBottom: '8pt',
                  lineHeight: 1.16,
                  borderTop: '1px solid #eee',
                  background: '#fafafa'
                }}
              >
                {doc.description}
              </div>
            )}
          </div>
        ))}
      </div>
    ))
  }

  return (
    <div
      style={{
        fontFamily: 'Arial, sans-serif',
        fontSize: '12pt',
        textAlign: 'justify',
        marginLeft: 0,
        marginRight: 0,
        marginTop: 0,
        marginBottom: '8pt',
        lineHeight: 1.16
      }}
    >
      {/* Gap 5 (pedido da Julia Buzzo, ver PR): capa isolada em pagina propria com
          <View pageBreakAfter> (mesma convencao de page-break CSS ja usada em
          pdfs/bradesco/pgr, ver components/pdf/View.js / 00.CoverSection.js), em vez de
          depender so da contagem de <br/> para empurrar o conteudo seguinte para a
          proxima pagina. Isso corrige a causa raiz do espaco em branco grande antes do
          "Sumario" relatado pela Julia: com @page { margin: 3cm 2cm 2cm 3cm } (round 3
          de #761) a area util da pagina encolheu, e a contagem antiga de <br/> (calibrada
          para as margens anteriores) passou a sobrar/faltar. Com o page-break explicito,
          o bloco seguinte SEMPRE comeca no topo da proxima pagina, independente da
          contagem de <br/> usada aqui so para espacamento vertical dentro da propria
          pagina -- essa contagem residual foi removida no seguimento abaixo apos causar overflow de pagina. */}
      <View pageBreakAfter>
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br /> <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <br />
        <h2 style={{ textAlign: 'center', fontFamily: 'Arial, sans-serif', fontSize: '16pt' }}>
          Manutenção Corretiva do Sistema de Detecção e Alarme de Incêndio – (SDAI) {place?.resume} -{' '}
          {MONTHS[currentMonth]?.toUpperCase()} - {currentYear} <br />
        </h2>
      {/* Seguimento (2º report da Julia Buzzo, mesmo PR): removida a contagem residual
          de <br/> que ficava aqui como "estimativa conservadora" pos-#761. Combinada
          com o pageBreakAfter, ela empurrava o conteudo alem de uma pagina inteira e
          transbordava para uma pagina extra quase em branco antes da quebra forcada
          entrar em acao -- essa era a causa das paginas em branco no meio do
          documento. O pageBreakAfter sozinho ja garante que o proximo bloco comece
          no topo da proxima pagina; nenhum <br/> de preenchimento e necessario aqui. */}
      </View>
      <View pageBreakAfter>
        <LineSeparator weight={4} color={COLORS.light3} padding={4} />
        <h2>
          Sumário
        </h2>
        <br />
        <br />
        <br />
        <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
          {summaryItems.map(item => (
            <li key={item.id}>
              <a href={`#${item.id}`} style={linkStyle}>
                <span>{item.title}</span>
                <span style={{ flex: 1, margin: '0 10px', borderBottom: '2px dotted #000', lineHeight: 0 }} />
                <span>{pageNumbers[item.id] ?? ''}</span>
              </a>

              {item.children?.map(child => (
                <a key={child.id} href={`#${child.id}`} style={linkStyle}>
                  <span style={{ textIndent: '10px' }}>{child.title}</span>
                  <span style={{ flex: 1, margin: '0 10px', borderBottom: '2px dotted #000', lineHeight: 0 }} />
                  <span>{pageNumbers[child.id] ?? ''}</span>
                </a>
              ))}
            </li>
          ))}
        </ul>
        {/* Seguimento (2º report da Julia Buzzo): mesma remocao do bloco de capa acima --
            a contagem residual de <br/> aqui transbordava a pagina do Sumario e, com o
            pageBreakAfter, gerava uma pagina extra quase em branco antes da Introducao.
            O pageBreakAfter ja garante que o proximo bloco comece na pagina certa. */}
      </View>
      {/* Âncora "zero" do motor de paginação do Sumário (ver `_shared/pagination.js`):
          marca o topo do conteúdo do corpo, que sempre começa na página 3 por causa do
          `pageBreakAfter` do Sumário logo acima. */}
      <div id={BODY_ORIGIN_ID} />
      <LineSeparator weight={4} color={COLORS.light3} padding={4} />
      <h3 id="introducao">1. Introdução</h3>
      {/* Gap 3: normalizado de textIndent:'2em' para '1.25cm' + resto do espaçamento de
          parágrafo, igual ao usado no restante do documento. */}
      <p
        style={{
          textAlign: 'justify',
          marginLeft: 0,
          marginRight: 0,
          textIndent: '1.25cm',
          marginTop: 0,
          marginBottom: '8pt',
          lineHeight: 1.16
        }}
      >
        Este Relatório tem como objetivo expressar as atividades realizadas pela empresa Easy Solutions, bem como as os
        problemas encontrados, correções realizadas, visando informar sempre as melhorias cabíveis ao sistema de acordo
        com a necessidade de cliente e atender a NBR 17240-2010 (Sistema de Detecção e Alarme de Incêndio, Projeto
        Instalação, Comissionamento e Manutenção do Sistema).
      </p>
      <View pageBreakBefore>
        <h3 id="servicos">2. Ferramentas utilizadas para Testar Detectores</h3>
      </View>
      {/* Gap 3: normalizado de textIndent:'2em' para '1.25cm' + resto do espaçamento de
          parágrafo, igual ao usado no restante do documento. */}
      <p
        style={{
          textAlign: 'justify',
          marginLeft: 0,
          marginRight: 0,
          textIndent: '1.25cm',
          marginTop: 0,
          marginBottom: '8pt',
          lineHeight: 1.16
        }}
      >
        A Easy Solutions possui os Kits para testar Detectores de fumaça e temperatura, Solo 100, equipamentos que
        atendem as Normas Internacionais de Fire Alarm, conforme figura abaixo.
      </p>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Image
          src="/pdfs/easysolutions/fire_alarm.png"
          maxHeight={'175px'}
          ignoreFixedSize
          alt="Figura 1 Alarme de Incendio"
        />
      </div>
      <View pageBreakBefore>
        <h3 id="manutencao">3. Serviços Realizados</h3>
      </View>
      <h3 id="central-incendio"> 3.1. Central de Incêndio </h3>
      ✓ Inspeção Visual <br />
      ✓ Limpeza da Central <br />
      ✓ Reaperto de Bornes e Conexões <br />
      ✓ Backup do sistema
      <br />
      <h3 id="detectores-fumaca">3.2. Detectores de Fumaça </h3>
      ✓ Inspeção Visual <br />
      ✓ Analise se o equipamento está funcionando <br />
      ✓ Teste com Gás de Fumaça para Detectores de fumaça <br />
      ✓ Teste com Soprador Térmico para Detectores de Temperatura <br />✓ Confirmação do Alarme de incêndio na Central
      <h3 id="acionadores-manuais">3.3. Acionadores Manuais </h3>
      ✓ Inspeção Visual <br />
      ✓ Analise se o equipamento está funcionando <br />
      ✓ Teste de Acionamento do Equipamento <br />
      ✓ Confirmação do Alarme de incêndio na Central <br />
      <h3 id="sirenes-modulo-comando">3.4. Sirenes e Módulo de Comando </h3>
      ✓ Inspeção Visual <br />
      ✓ Analise se o equipamento está funcionando <br />
      ✓ Teste de acionamento de Sirene
      <br />
      <View pageBreakBefore>
        <h3 id="teste-central">4. Status da Central</h3>
      </View>
      {question548?.question?.name && (
        <section>
          {/* Gap 2 (PR #761 seguimento, pedido da Julia Buzzo): estes ~9 blocos <p> inline
              (seções 4, 5 e 6) só tinham recebido o textAlign:'justify' da 1ª rodada de
              #761 — faltava o resto do espaçamento de parágrafo (recuo de 1ª linha,
              margens, line-height) aplicado aos demais parágrafos do documento na 2ª
              rodada. fontFamily não é repetido: herda de 'Arial, sans-serif' do <div>
              raiz, mesmo padrão já usado nos outros parágrafos deste arquivo (linhas
              530-568). */}
          <p
            style={{
              textAlign: 'justify',
              marginLeft: 0,
              marginRight: 0,
              textIndent: '1.25cm',
              marginTop: 0,
              marginBottom: '8pt',
              lineHeight: 1.16
            }}
          >
            <strong>{question548?.question?.name}:</strong>{' '}
            {Array.isArray(question548?.value)
              ? ''
              : typeof question548?.value === 'boolean'
              ? question548.value
                ? 'Sim'
                : 'Não'
              : question548?.value ?? 'N/A'}
          </p>

          {(Array.isArray(question548?.value) ? question548?.value : question548?.documents)?.length > 0 && (
            <Grid wrap gap={0.5} align="center" justify="center">
              {renderImages(Array.isArray(question548?.value) ? question548?.value : question548?.documents)}
            </Grid>
          )}
        </section>
      )}
      {question549?.question?.name && (
        <section>
          {/* Gap 2 (PR #761 seguimento, pedido da Julia Buzzo): estes ~9 blocos <p> inline
              (seções 4, 5 e 6) só tinham recebido o textAlign:'justify' da 1ª rodada de
              #761 — faltava o resto do espaçamento de parágrafo (recuo de 1ª linha,
              margens, line-height) aplicado aos demais parágrafos do documento na 2ª
              rodada. fontFamily não é repetido: herda de 'Arial, sans-serif' do <div>
              raiz, mesmo padrão já usado nos outros parágrafos deste arquivo (linhas
              530-568). */}
          <p
            style={{
              textAlign: 'justify',
              marginLeft: 0,
              marginRight: 0,
              textIndent: '1.25cm',
              marginTop: 0,
              marginBottom: '8pt',
              lineHeight: 1.16
            }}
          >
            <strong>{question549?.question?.name}:</strong>{' '}
            {Array.isArray(question549?.value)
              ? ''
              : typeof question549?.value === 'boolean'
              ? question549.value
                ? 'Sim'
                : 'Não'
              : question549?.value ?? 'N/A'}
          </p>

          {(Array.isArray(question549?.value) ? question549?.value : question549?.documents)?.length > 0 && (
            <Grid wrap gap={0.5} align="center" justify="center">
              {renderImages(Array.isArray(question549?.value) ? question549?.value : question549?.documents)}
            </Grid>
          )}
        </section>
      )}
      {question550?.question?.name && (
        <section>
          {/* Gap 2 (PR #761 seguimento, pedido da Julia Buzzo): estes ~9 blocos <p> inline
              (seções 4, 5 e 6) só tinham recebido o textAlign:'justify' da 1ª rodada de
              #761 — faltava o resto do espaçamento de parágrafo (recuo de 1ª linha,
              margens, line-height) aplicado aos demais parágrafos do documento na 2ª
              rodada. fontFamily não é repetido: herda de 'Arial, sans-serif' do <div>
              raiz, mesmo padrão já usado nos outros parágrafos deste arquivo (linhas
              530-568). */}
          <p
            style={{
              textAlign: 'justify',
              marginLeft: 0,
              marginRight: 0,
              textIndent: '1.25cm',
              marginTop: 0,
              marginBottom: '8pt',
              lineHeight: 1.16
            }}
          >
            <strong>{question550?.question?.name}:</strong>{' '}
            {Array.isArray(question550?.value)
              ? ''
              : typeof question550?.value === 'boolean'
              ? question550.value
                ? 'Sim'
                : 'Não'
              : question550?.value ?? 'N/A'}
          </p>

          {(Array.isArray(question550?.value) ? question550?.value : question550?.documents)?.length > 0 && (
            <Grid wrap gap={0.5} align="center" justify="center">
              {renderImages(Array.isArray(question550?.value) ? question550?.value : question550?.documents)}
            </Grid>
          )}
        </section>
      )}
      {question551?.question?.name && (
        <section>
          {/* Gap 2 (PR #761 seguimento, pedido da Julia Buzzo): estes ~9 blocos <p> inline
              (seções 4, 5 e 6) só tinham recebido o textAlign:'justify' da 1ª rodada de
              #761 — faltava o resto do espaçamento de parágrafo (recuo de 1ª linha,
              margens, line-height) aplicado aos demais parágrafos do documento na 2ª
              rodada. fontFamily não é repetido: herda de 'Arial, sans-serif' do <div>
              raiz, mesmo padrão já usado nos outros parágrafos deste arquivo (linhas
              530-568). */}
          <p
            style={{
              textAlign: 'justify',
              marginLeft: 0,
              marginRight: 0,
              textIndent: '1.25cm',
              marginTop: 0,
              marginBottom: '8pt',
              lineHeight: 1.16
            }}
          >
            <strong>{question551?.question?.name}:</strong>{' '}
            {Array.isArray(question551?.value)
              ? ''
              : typeof question551?.value === 'boolean'
              ? question551.value
                ? 'Sim'
                : 'Não'
              : question551?.value ?? 'N/A'}
          </p>

          {(Array.isArray(question551?.value) ? question551?.value : question551?.documents)?.length > 0 && (
            <Grid wrap gap={0.5} align="center" justify="center">
              {renderImages(Array.isArray(question551?.value) ? question551?.value : question551?.documents)}
            </Grid>
          )}
        </section>
      )}
      {question567?.question?.name && (
        <section>
          {/* Gap 2 (PR #761 seguimento, pedido da Julia Buzzo): estes ~9 blocos <p> inline
              (seções 4, 5 e 6) só tinham recebido o textAlign:'justify' da 1ª rodada de
              #761 — faltava o resto do espaçamento de parágrafo (recuo de 1ª linha,
              margens, line-height) aplicado aos demais parágrafos do documento na 2ª
              rodada. fontFamily não é repetido: herda de 'Arial, sans-serif' do <div>
              raiz, mesmo padrão já usado nos outros parágrafos deste arquivo (linhas
              530-568). */}
          <p
            style={{
              textAlign: 'justify',
              marginLeft: 0,
              marginRight: 0,
              textIndent: '1.25cm',
              marginTop: 0,
              marginBottom: '8pt',
              lineHeight: 1.16
            }}
          >
            <strong>{question567?.question?.name}:</strong>{' '}
            {Array.isArray(question567?.value)
              ? ''
              : typeof question567?.value === 'boolean'
              ? question567.value
                ? 'Sim'
                : 'Não'
              : question567?.value ?? 'N/A'}
          </p>

          {(Array.isArray(question567?.value) ? question567?.value : question567?.documents)?.length > 0 && (
            <Grid wrap gap={0.5} align="center" justify="center">
              {renderImages(Array.isArray(question567?.value) ? question567?.value : question567?.documents)}
            </Grid>
          )}
        </section>
      )}
      {question552?.question?.name && (
        <section>
          {/* Gap 2 (PR #761 seguimento, pedido da Julia Buzzo): estes ~9 blocos <p> inline
              (seções 4, 5 e 6) só tinham recebido o textAlign:'justify' da 1ª rodada de
              #761 — faltava o resto do espaçamento de parágrafo (recuo de 1ª linha,
              margens, line-height) aplicado aos demais parágrafos do documento na 2ª
              rodada. fontFamily não é repetido: herda de 'Arial, sans-serif' do <div>
              raiz, mesmo padrão já usado nos outros parágrafos deste arquivo (linhas
              530-568). */}
          <p
            style={{
              textAlign: 'justify',
              marginLeft: 0,
              marginRight: 0,
              textIndent: '1.25cm',
              marginTop: 0,
              marginBottom: '8pt',
              lineHeight: 1.16
            }}
          >
            <strong>{question552?.question?.name}:</strong>{' '}
            {Array.isArray(question552?.value)
              ? ''
              : typeof question552?.value === 'boolean'
              ? question552.value
                ? 'Sim'
                : 'Não'
              : question552?.value ?? 'N/A'}
          </p>

          {(Array.isArray(question552?.value) ? question552?.value : question552?.documents)?.length > 0 && (
            <Grid wrap gap={0.5} align="center" justify="center">
              {renderImages(Array.isArray(question552?.value) ? question552?.value : question552?.documents)}
            </Grid>
          )}
        </section>
      )}
      <View pageBreakBefore>
        <h3 id="saida">5. Manutenção preventiva realizada</h3>
      </View>
      {manutencaoPreventivaQuestions.map((q, index) => (
        <section key={q.id}>
          {/* Gap 2 (PR #761 seguimento, pedido da Julia Buzzo): estes ~9 blocos <p> inline
              (seções 4, 5 e 6) só tinham recebido o textAlign:'justify' da 1ª rodada de
              #761 — faltava o resto do espaçamento de parágrafo (recuo de 1ª linha,
              margens, line-height) aplicado aos demais parágrafos do documento na 2ª
              rodada. fontFamily não é repetido: herda de 'Arial, sans-serif' do <div>
              raiz, mesmo padrão já usado nos outros parágrafos deste arquivo (linhas
              530-568). */}
          <p
            style={{
              textAlign: 'justify',
              marginLeft: 0,
              marginRight: 0,
              textIndent: '1.25cm',
              marginTop: 0,
              marginBottom: '8pt',
              lineHeight: 1.16
            }}
          >
            <View pageBreakBefore={index > 0}>
              <h3 id={q.id}>
                5.{index + 1}. {q.data.question.name}:
              </h3>
            </View>{' '}
            {Array.isArray(q.data.value)
              ? q.data.value
                  .map(item => item.name)
                  .filter(Boolean)
                  .join(', ')
              : typeof q.data.value === 'boolean'
              ? q.data.value
                ? 'Sim'
                : 'Não'
              : q.data.value ?? 'N/A'}
            {q.data.notes && (
              <div>
                <Separator weight={4} />
                <strong>Observações:</strong> {q.data.notes}
              </div>
            )}
          </p>

          {(Array.isArray(q?.data?.value) ? q?.data?.value : q?.data?.documents)?.length > 0 && (
            <Grid wrap gap={0.5} align="center" justify="center">
              {renderImages(getFiles(q))}
            </Grid>
          )}
        </section>
      ))}
      <View pageBreakBefore>
        <h3 id="conclusao">6. Conclusão </h3>
      </View>
      {question570?.question?.name && (
        <section>
          {/* Gap 2 (PR #761 seguimento, pedido da Julia Buzzo): estes ~9 blocos <p> inline
              (seções 4, 5 e 6) só tinham recebido o textAlign:'justify' da 1ª rodada de
              #761 — faltava o resto do espaçamento de parágrafo (recuo de 1ª linha,
              margens, line-height) aplicado aos demais parágrafos do documento na 2ª
              rodada. fontFamily não é repetido: herda de 'Arial, sans-serif' do <div>
              raiz, mesmo padrão já usado nos outros parágrafos deste arquivo (linhas
              530-568). */}
          <p
            style={{
              textAlign: 'justify',
              marginLeft: 0,
              marginRight: 0,
              textIndent: '1.25cm',
              marginTop: 0,
              marginBottom: '8pt',
              lineHeight: 1.16
            }}
          >
            <strong>{question570?.question?.name}:</strong>{' '}
            {Array.isArray(question570?.value) ? '' : question570?.value}
          </p>
        </section>
      )}
      {question555?.question?.name && (
        <section>
          {/* Gap 2 (PR #761 seguimento, pedido da Julia Buzzo): estes ~9 blocos <p> inline
              (seções 4, 5 e 6) só tinham recebido o textAlign:'justify' da 1ª rodada de
              #761 — faltava o resto do espaçamento de parágrafo (recuo de 1ª linha,
              margens, line-height) aplicado aos demais parágrafos do documento na 2ª
              rodada. fontFamily não é repetido: herda de 'Arial, sans-serif' do <div>
              raiz, mesmo padrão já usado nos outros parágrafos deste arquivo (linhas
              530-568). */}
          <p
            style={{
              textAlign: 'justify',
              marginLeft: 0,
              marginRight: 0,
              textIndent: '1.25cm',
              marginTop: 0,
              marginBottom: '8pt',
              lineHeight: 1.16
            }}
          >
            <strong>{question555?.question?.name}:</strong>{' '}
            {Array.isArray(question555?.value)
              ? ''
              : typeof question555?.value === 'boolean'
              ? question555.value
                ? 'Sim'
                : 'Não'
              : question555?.value ?? 'N/A'}
          </p>

          {(Array.isArray(question555?.value) ? question555?.value : question555?.documents)?.length > 0 && (
            <Grid wrap gap={0.5} align="center" justify="center">
              {renderImages(Array.isArray(question555?.value) ? question555?.value : question555?.documents)}
            </Grid>
          )}
        </section>
      )}
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      {question571?.question?.name && (
        <section
          style={{
            textAlign: 'justify',
            marginLeft: 0,
            marginRight: 0,
            textIndent: '1.25cm',
            marginTop: 0,
            marginBottom: '8pt',
            lineHeight: 1.16
          }}
        >
          <strong>
            <i>{Array.isArray(question571?.value) ? '' : question571?.value}</i>
          </strong>
        </section>
      )}
      {question572?.question?.name && (
        <section
          style={{
            textAlign: 'justify',
            marginLeft: 0,
            marginRight: 0,
            textIndent: '1.25cm',
            marginTop: 0,
            marginBottom: '8pt',
            lineHeight: 1.16
          }}
        >
          <strong>
            <i>
              {Array.isArray(question572?.value)
                ? ''
                : new Date(question572?.value + 'T00:00:00').toLocaleDateString('pt-BR')}
            </i>
          </strong>
        </section>
      )}
      <Separator weight={5} />
    </div>
  )
}

export default Content
